'use client'

import { useEffect, useState } from 'react'
import { GoogleAnalytics } from '@next/third-parties/google'
import { GA_MEASUREMENT_ID } from '@/components/CookieConsent/config'

/*
 * Defer gtag.js until the browser is idle (or the first user interaction) so it
 * doesn't compete with hero/LCP work during initial load. ~174 KiB of GA/GTM JS
 * was loading eagerly and showing up as unused JS on first paint.
 *
 * Safety:
 *  - Google Consent Mode v2 defaults are still set `beforeInteractive` in the
 *    document head, so all signals stay 'denied' until the user accepts.
 *  - `gtag()` is defined by that same head script and pushes to `dataLayer`, so
 *    any page_view / event fired before gtag.js loads is queued and replayed once
 *    the script is in — no tracking is lost by delaying the load.
 */
export default function DeferredGoogleAnalytics() {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false
    const start = () => {
      if (!cancelled) setReady(true)
    }

    const ric = (window as unknown as { requestIdleCallback?: typeof requestIdleCallback })
      .requestIdleCallback
    let idleId: number | undefined
    let timeoutId: number | undefined
    if (typeof ric === 'function') {
      idleId = ric(start, { timeout: 3000 })
    } else {
      timeoutId = window.setTimeout(start, 2000)
    }

    // Also load on the first real interaction, in case it happens before idle.
    const onInteract = () => start()
    window.addEventListener('pointerdown', onInteract, { once: true, passive: true })
    window.addEventListener('keydown', onInteract, { once: true })

    return () => {
      cancelled = true
      if (idleId !== undefined) {
        const cancelIdle = (window as unknown as { cancelIdleCallback?: (id: number) => void })
          .cancelIdleCallback
        cancelIdle?.(idleId)
      }
      if (timeoutId !== undefined) window.clearTimeout(timeoutId)
      window.removeEventListener('pointerdown', onInteract)
      window.removeEventListener('keydown', onInteract)
    }
  }, [])

  if (!ready) return null

  return <GoogleAnalytics gaId={GA_MEASUREMENT_ID} />
}
